import React, { useState } from 'react'
import Image from '@components/atoms/Image'
import Icon from '@components/atoms/Icon'
import Modal from '@components/molecules/Modal'
import { useSelector } from 'react-redux'
import { RootState } from '@redux/store'
import SizeGuideTable from './SizeGuideTable'
import sizeGuideDataJSON from './sizeguides.json'

export type InputData = {
  columns?: string[]
  entryLabel?: string[]
  entrySizes?: string[]
  apparelFit?: string
  size?: string
  image?: string
  frame_width?: { measurement: string }
  frame_height?: { measurement: string }
  nose_opening?: { measurement: string }
}

export type OutputData = {
  data: {
    size: string
    [key: string]: { measurement: string } | string
  }[]
}

interface SizeGuideBlock {
  heading: string
  description: string
  descriptionMetric: string
}

export interface SizeGuideSectionProps {
  variantImage?: {
    src: string
    alt: string
  }
  firstSizeGuideIconTitle: string
  secondSizeGuideIconTitle: string
  thirdSizeGuideIconTitle: string
  fourthSizeGuideIconTitle: string
  fourthSizeGuideIconDescription: string
  blocks: SizeGuideBlock[]
  ctaText?: string
  ctaLink?: string
  ctaVariant: string
  isMobile: boolean
  isEyewear?: boolean
  modalBgColor?: string
  modalBgImage?: string
  eyewearModalImage?: string
  productSizeGuide: InputData
}

const SizeGuideSection: React.FC<SizeGuideSectionProps> = ({
  variantImage,
  firstSizeGuideIconTitle,
  secondSizeGuideIconTitle,
  thirdSizeGuideIconTitle,
  fourthSizeGuideIconTitle,
  blocks,
  ctaText,
  ctaLink,
  ctaVariant,
  isMobile,
  isEyewear,
  modalBgColor,
  modalBgImage,
  eyewearModalImage,
  productSizeGuide,
}) => {
  const PDPVariant = useSelector((state: RootState) => state.PDP.PDPVariant)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => setIsModalOpen(false)

  // Fallback data
  const fallbackVariantImage = PDPVariant?.sizeGuideData?.variantImage || variantImage
  const fallbackCtaText = PDPVariant?.sizeGuideData?.ctaText || ctaText
  const fallbackCtaLink = PDPVariant?.sizeGuideData?.ctaLink || ctaLink
  const fallbackCtaVariant = PDPVariant?.sizeGuideData?.ctaVariant || ctaVariant

  const fallbackBlocks = PDPVariant?.sizeGuideData?.blocks ? PDPVariant?.sizeGuideData?.blocks : blocks

  const modalBackgroundStyle: React.CSSProperties = {
    background: `${modalBgColor}, url(${modalBgImage}) 50% / cover no-repeat, lightgray`,
  }

  const modalContent = (
    <div
      style={modalBackgroundStyle}
      className="relative rounded-b-lg pdp-gallery-modal mac-scroll mt-5 max-w-3xl w-full overflow-y-auto max-h-[90vh] lg:max-w-screen-lg"
    >
      <div className="flex flex-col px-2 lg:px-5">
        <div>
          <div>
            <h3 className="text-black h1 py-[10px] lg:py-0">{window.DEPT.Strings.product.howToMeasure}</h3>
            <Image
              alt="How to Measure"
              src={eyewearModalImage!}
              srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280 }}
              classes="w-full max-h-[374px] object-contain py-10 lg:py-15"
              blendMode="multiply"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="grid grid-rows-2 h-[170px] gap-4 text-left justify-between bg-white p-4 shadow-lg border border-grey-10">
              <span className="self-stretch text-[20px] text-inverse">#1</span>
              <p className="self-end text-black body-xs">{window.DEPT.Strings.product.sizeGuideStepOne}</p>
            </div>
            <div className="grid grid-rows-2 h-[170px] gap-4 text-left justify-between bg-white p-4 shadow-lg border border-grey-10">
              <span className="self-stretch text-[20px] text-inverse">#2</span>
              <p className="self-end text-black body-xs">{window.DEPT.Strings.product.sizeGuideStepTwo}</p>
            </div>
            <div className="grid grid-rows-2 h-[170px] gap-4 text-left justify-between bg-white p-4 shadow-lg border border-grey-10">
              <span className="self-stretch text-[20px] text-inverse">#3</span>
              <p className="self-end text-black body-xs">{window.DEPT.Strings.product.sizeGuideStepThree}</p>
            </div>
          </div>

          <div className="min-w-full text-left bg-white px-4 shadow-lg border border-grey-10 overflow-x-auto">
            {Object.entries(sizeGuideDataJSON).map(([category, data]) => (
              <SizeGuideTable key={category} isEyewear={isEyewear} category={category} data={data} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <div className="grid grid-cols-1 items-center">
        <div className="pb-4">
          <Image
            alt={fallbackVariantImage!.alt}
            src={fallbackVariantImage!.src}
            srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280 }}
            width={1536}
            classes="max-h-full max-w-full"
          />
        </div>

        <div className="grid grid-rows-4 gap-y-4 items-center">
          {fallbackBlocks?.map((block, index) => {
            let dynamicTitle = ''
            switch (index) {
              case 0:
                dynamicTitle = firstSizeGuideIconTitle
                break
              case 1:
                dynamicTitle = secondSizeGuideIconTitle
                break
              case 2:
                dynamicTitle = thirdSizeGuideIconTitle
                break
              case 3:
                dynamicTitle = fourthSizeGuideIconTitle
                break
              default:
                dynamicTitle = block.heading
            }

            return (
              <div key={index} className="grid grid-cols-2 items-center px-4">
                {block?.heading && (
                  <div className="p-4 flex items-center justify-center">
                    <Icon name={`icon-${block.heading.toLowerCase().replace(' ', '-')}`} />
                  </div>
                )}

                <div className="text-left">
                  {dynamicTitle && <h3 className="text-black h1 text-[24px] leading-8">{dynamicTitle}</h3>}

                  <p className="text-black body-md lg:text-[16px]">
                    {block?.description && (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: block.description || '',
                        }}
                      ></span>
                    )}
                    {block?.descriptionMetric && (
                      <>
                        <br />
                        {block.descriptionMetric}
                      </>
                    )}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        <div className="py-10">
          <a onClick={openModal} title={fallbackCtaText} className={`button button--${fallbackCtaVariant}`}>
            <div className="rectangle-1"></div>
            <div className="button-text">{fallbackCtaText}</div>
          </a>
        </div>

        <Modal isOpen={isModalOpen} onClose={closeModal}>
          {modalContent}
        </Modal>
      </div>
    )
  }

  if (isEyewear) {
    return (
      <>
        <div className="grid grid-cols-12 gap-x-16 items-center">
          <div className="grid grid-rows-2 col-span-3 gap-y-16 items-center pl-10">
            <div className="flex flex-col pt-12">
              <div className="p-4 flex items-center justify-center">
                <Icon name="icon-frame-height" />
              </div>
              <h3 className="text-black h1 lg:text-[24px] lg:leading-8">
                {firstSizeGuideIconTitle ? firstSizeGuideIconTitle : fallbackBlocks[0].heading}
              </h3>
              <p className="text-black body-md lg:text-[16px]">
                {fallbackBlocks[0].description}
                <br />
                {fallbackBlocks[0].descriptionMetric}
              </p>
            </div>
            <div className="flex flex-col pb-12">
              <div className="p-4 flex items-center justify-center">
                <Icon name="icon-nose-opening" />
              </div>
              <h3 className="text-black h1 lg:text-[24px] lg:leading-8">
                {secondSizeGuideIconTitle ? secondSizeGuideIconTitle : fallbackBlocks[1].heading}
              </h3>
              <p className="text-black body-md lg:text-[16px]">
                {fallbackBlocks[1].description}
                <br />
                {fallbackBlocks[1].descriptionMetric}
              </p>
            </div>
          </div>

          <div className="flex items-center col-span-6 justify-center">
            <Image
              alt={fallbackVariantImage!.alt}
              src={fallbackVariantImage!.src}
              srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280 }}
              width={1536}
              classes="max-h-full max-w-full"
              blendMode="multiply"
            />
          </div>

          <div className="grid grid-rows-2 col-span-3 gap-y-16 items-center pr-10">
            <div className="flex flex-col pt-12">
              <div className="p-4 flex items-center justify-center">
                <Icon name="icon-frame-length" />
              </div>
              <h3 className="text-black h1 lg:text-[24px] lg:leading-8">
                {thirdSizeGuideIconTitle ? thirdSizeGuideIconTitle : fallbackBlocks[2].heading}
              </h3>
              <p className="text-black body-md lg:text-[16px]">
                {fallbackBlocks[2].description}
                <br />
                {fallbackBlocks[2].descriptionMetric}
              </p>
            </div>
            <div className="flex flex-col pb-12">
              <div className="p-4 flex items-center justify-center">
                <Icon name="icon-temple-length" />
              </div>
              <h3 className="text-black h1 lg:text-[24px] lg:leading-8">
                {fourthSizeGuideIconTitle ? fourthSizeGuideIconTitle : fallbackBlocks[3].heading}
              </h3>
              <p className="text-black body-md lg:text-[16px]">
                {fallbackBlocks[3].description}
                <br />
                {fallbackBlocks[3].descriptionMetric}
              </p>
            </div>
          </div>
        </div>
        <div className="py-10">
          <a
            onClick={openModal}
            data-href={fallbackCtaLink}
            title={fallbackCtaText}
            className={`button button--${fallbackCtaVariant}`}
          >
            <div className="rectangle-1"></div>
            <div className="button-text">{fallbackCtaText}</div>
          </a>
        </div>
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          {modalContent}
        </Modal>
      </>
    )
  } else {
    const generateSizeJSON = (data: { columns: string[]; entrySizes: string[]; entryLabel: string[] }): OutputData => {
      const { columns, entrySizes, entryLabel } = data

      // Ensure `columns`, `entrySizes`, and `entryLabel` have content
      if (
        !columns ||
        !entrySizes ||
        !entryLabel ||
        columns.length === 0 ||
        entrySizes.length === 0 ||
        entryLabel.length === 0
      ) {
        console.warn('Missing columns, entry sizes, or entry labels in data')
        return { data: [] }
      }

      // Split `entrySizes` strings into arrays
      const parsedEntrySizes = entrySizes.map((sizes) => sizes.split(', ').map((size) => size.trim()))

      // Combine labels and sizes into the desired JSON structure
      const dataArray = parsedEntrySizes.map((sizes, rowIndex) => {
        const sizeData: { [key: string]: { measurement: string } | string } = {
          size: entryLabel[rowIndex], // Use `entryLabel` for row names
        }

        sizes.forEach((size, colIndex) => {
          sizeData[columns[colIndex]] = { measurement: size } // Map column headers to sizes
        })

        return sizeData
      })

      return { data: dataArray } as OutputData
    }

    const result = generateSizeJSON({
      columns: productSizeGuide.columns ?? [],
      entrySizes: productSizeGuide.entrySizes ?? [],
      entryLabel: productSizeGuide.entryLabel ?? [],
    })
    return (
      <>
        <div className="grid grid-cols-1 gap-10 pb-10 px-4 lg:p-10 lg:grid-cols-2">
          <div className="py-6 flex justify-center items-center w-full lg:py-0">
            <Image
              alt="Apparel Size Guide"
              src={productSizeGuide.image!}
              srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280 }}
              classes="h-[540px] w-full object-contain"
            />
          </div>
          <div className="min-w-full text-left overflow-x-auto">
            <SizeGuideTable
              isEyewear={isEyewear}
              category={PDPVariant?.productTitle}
              columns={productSizeGuide.columns}
              apparelFit={productSizeGuide.apparelFit}
              data={result.data}
            />
          </div>
        </div>
      </>
    )
  }
}

export default SizeGuideSection
